import { Box, List, ListItem, ListItemButton } from "@mui/material";
import { useEffect, useRef } from "react";

import type { MenuOption } from "@lexical/react/LexicalTypeaheadMenuPlugin";

type Props<T> = {
  options: T[];
  selectedIndex: number | null;
  setHighlightedIndex: (index: number) => void;
  selectOptionAndCleanUp: (option: T) => void;
  renderOption: (option: T) => React.JSX.Element | string;
} & React.ComponentProps<typeof Box>;
export const TypeaheadOptionsList = <T extends MenuOption>({
  options,
  selectedIndex,
  setHighlightedIndex,
  selectOptionAndCleanUp,
  renderOption,
  ...props
}: Props<T>): React.JSX.Element => {
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    itemRefs.current[selectedIndex ?? 0]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedIndex]);

  return (
    <Box sx={{ marginTop: 3, minWidth: 120 }} {...props}>
      <List
        sx={{
          margin: 0,
          padding: 0,
          display: "inline-block",
          backgroundColor: "background.paper",
          overflow: "auto",
          maxHeight: 200,
          zIndex: 2,
        }}
      >
        {options.map((option, i: number) => (
          <ListItem
            sx={{ margin: 0, padding: 0, width: "100%" }}
            // oxlint-disable-next-line react/no-array-index-key -- Options are refiltered while typing and rows hold ripple state; keying by option changes that
            key={i}
            ref={(e) => option.setRefElement(e)}
            onMouseEnter={() => {
              setHighlightedIndex(i);
            }}
          >
            <ListItemButton
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              selected={selectedIndex === i}
              onClick={() => {
                setHighlightedIndex(i);
                selectOptionAndCleanUp(option);
              }}
            >
              {renderOption(option)}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};
